import type { Stripe, StripeElements, StripePaymentElement } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { createSignal, onCleanup, onMount, Show } from "solid-js";
import type { FormData } from "./types";
import { createDepositIntent } from "./api";
import { formatCurrency, useI18n } from "./i18n";
import { Notice } from "./_notice";

type DepositStepProps = {
  formData: FormData;
  locationId: string;
  publishableKey: string | null;
  /** Set only when this location surcharges; disclosed before the charge. */
  cardFeePercentage: number | null;
  amountCents: number;
  locale: string;
  onPaid: () => void;
  onBack: () => void;
  errors: string[];
};

/** How long to wait for Stripe's card field before calling it unavailable. */
const CARD_READY_TIMEOUT_MS = 10_000;

/** 3 -> "3", 2.5 -> "2.5"; the rate is stored as a decimal. */
function formatPercentage(value: number): string {
  return String(Number(value.toFixed(2)));
}

/**
 * The last step before the booking is attempted. Paying and booking are two
 * requests and the second can still be refused (the chair went while we took
 * the money), so this hands control back to the wizard only once Stripe has
 * confirmed — and submit re-checks with Stripe before it books either way.
 */
export function DepositStep(props: DepositStepProps) {
  const t = useI18n();

  const [stripe, setStripe] = createSignal<Stripe | null>(null);
  const [elements, setElements] = createSignal<StripeElements | null>(null);
  const [cardComplete, setCardComplete] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  // Once the card has gone through, the booking is the only thing left. If that
  // is refused the patient stays here reading why — and must not be offered a
  // button that would charge them a second time.
  const [paid, setPaid] = createSignal(false);
  const [loadFailed, setLoadFailed] = createSignal(false);
  const [cardErrors, setCardErrors] = createSignal<string[]>([]);
  const [surcharge, setSurcharge] = createSignal<{ fee: string; total: string } | null>(null);

  let mountPoint: HTMLDivElement | undefined;
  let paymentElement: StripePaymentElement | undefined;
  let readyTimer: number | undefined;
  // Not a signal: nothing renders it, it only decides whether the timeout below
  // has anything to complain about.
  let cardReady = false;
  let disposed = false;

  const formattedAmount = () => formatCurrency(props.amountCents, props.locale);

  const mountStripe = async () => {
    if (!props.publishableKey) {
      setLoadFailed(true);
      return;
    }

    const loaded = await loadStripe(props.publishableKey);
    if (disposed) return;
    if (!loaded) {
      setLoadFailed(true);
      return;
    }

    // paymentMethodCreation "manual" so the card is turned into a payment
    // method before the server opens the charge, mirroring static checkout.
    const els = loaded.elements({
      appearance: { theme: "stripe" },
      mode: "payment",
      currency: "usd",
      paymentMethodTypes: ["card"],
      paymentMethodCreation: "manual",
      amount: props.amountCents,
    });

    paymentElement = els.create("payment");
    paymentElement.mount(mountPoint!);
    paymentElement.on("change", (event) => setCardComplete(event.complete));
    // Stripe.js can load and the card field still fail to come up — a bad
    // publishable key, or Stripe being down. Without this the patient gets a
    // blank gap and a button that never enables, with nothing said.
    paymentElement.on("loaderror", () => setLoadFailed(true));
    paymentElement.on("ready", () => {
      clearTimeout(readyTimer);
      cardReady = true;
    });

    // Belt to loaderror's braces: a session that never resolves emits neither
    // ready nor loaderror, and the patient is left staring at an empty box.
    // Observed with an invalid key, which is exactly how it looks when Stripe
    // is unreachable.
    readyTimer = window.setTimeout(() => {
      if (!disposed && !cardReady) setLoadFailed(true);
    }, CARD_READY_TIMEOUT_MS);

    setStripe(loaded);
    setElements(els);
  };

  onMount(() => void mountStripe());

  onCleanup(() => {
    disposed = true;
    clearTimeout(readyTimer);
    paymentElement?.unmount();
    paymentElement = undefined;
  });

  const pay = async () => {
    const s = stripe();
    const els = elements();
    if (!s || !els || busy() || paid()) return;

    setBusy(true);
    setCardErrors([]);

    try {
      const submitted = await els.submit();
      if (submitted.error) {
        setCardErrors([submitted.error.message ?? t("deposit_card_error")]);
        return;
      }

      const created = await s.createPaymentMethod({ elements: els });
      if (created.error) {
        setCardErrors([created.error.message ?? t("deposit_card_error")]);
        return;
      }

      const intent = await createDepositIntent(
        props.locationId,
        props.formData,
        created.paymentMethod.id,
        props.locale,
      );
      if (intent.status === "refused") {
        setCardErrors(intent.errors.length > 0 ? intent.errors : [t("deposit_card_error")]);
        return;
      }

      if (intent.surchargeAmount) {
        setSurcharge({ fee: intent.surchargeAmount, total: intent.totalAmount });
      }

      const confirmed = await s.confirmPayment({
        elements: els,
        clientSecret: intent.clientSecret,
        confirmParams: {},
        redirect: "if_required",
      });

      if (confirmed.error) {
        setCardErrors([confirmed.error.message ?? t("deposit_card_error")]);
        return;
      }

      const status = confirmed.paymentIntent.status;
      if (status === "succeeded") {
        setPaid(true);
        props.onPaid();
        return;
      }

      // "processing" is a card that has not cleared yet. Booking on it would
      // be booking on a payment that can still fail, and submitting would just
      // be refused — so stop here and say so truthfully. Still `paid`, because
      // the money is in flight and must not be taken twice.
      if (status === "processing") {
        setPaid(true);
        setCardErrors([t("deposit_processing")]);
        return;
      }

      setCardErrors([t("deposit_card_error")]);
    } catch {
      setCardErrors([t("deposit_card_error")]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="flex flex-col gap-5">
      <h2 class="text-xl font-semibold text-typography-primary">{t("deposit_title")}</h2>

      <p class="text-sm text-typography-secondary">
        {t("deposit_explainer", { amount: formattedAmount() })}
      </p>

      {/* Said up front, not after the card is charged: the exact fee depends on
          whether the card is credit or debit, but the rate does not. */}
      <Show when={props.cardFeePercentage}>
        {(pct) => (
          <p class="text-sm text-typography-secondary">
            {t("deposit_card_fee_notice", { percentage: formatPercentage(pct()) })}
          </p>
        )}
      </Show>

      <div class="flex flex-col gap-2 rounded-xl border border-brand-subtle p-4">
        <div class="flex items-baseline justify-between gap-4">
          <span class="text-sm text-typography-secondary">{t("deposit_due")}</span>
          <span class="text-sm font-medium text-typography-primary">{formattedAmount()}</span>
        </div>
        <Show when={surcharge()}>
          {(fees) => (
            <>
              <div class="flex items-baseline justify-between gap-4">
                <span class="text-sm text-typography-secondary">{t("deposit_card_fee")}</span>
                <span class="text-sm font-medium text-typography-primary">{fees().fee}</span>
              </div>
              <div class="flex items-baseline justify-between gap-4 border-t border-brand-subtle pt-2">
                <span class="text-sm font-semibold text-typography-primary">
                  {t("deposit_total")}
                </span>
                <span class="text-sm font-semibold text-typography-primary">{fees().total}</span>
              </div>
            </>
          )}
        </Show>
      </div>

      <Show when={!loadFailed()} fallback={<Notice messages={[t("deposit_unavailable")]} />}>
        <div class="min-h-[120px]">
          <Show when={!stripe()}>
            <p class="text-sm text-typography-secondary">{t("deposit_loading")}</p>
          </Show>
          {/* Stripe mounts into this and requires it EMPTY — putting the
              loading text inside it stops the card field ever coming up. */}
          <div ref={mountPoint} />
        </div>
      </Show>

      <Show when={cardErrors().length > 0 || props.errors.length > 0}>
        <Notice messages={[...cardErrors(), ...props.errors]} />
      </Show>

      <div class="flex items-center gap-3 pt-1">
        <button
          type="button"
          class="rounded-full border border-brand-subtle px-4 py-2.5 text-sm font-medium text-typography-primary transition-colors hover:bg-brand-light active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          disabled={busy() || paid()}
          onClick={() => props.onBack()}
        >
          {t("back")}
        </button>
        <button
          type="button"
          class="ml-auto rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all brand-button-bg hover:brand-button-bg-active active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!cardComplete() || busy() || paid() || loadFailed()}
          onClick={() => void pay()}
        >
          {busy() || paid() ? t("deposit_paying") : t("deposit_pay_and_book")}
        </button>
      </div>
    </div>
  );
}
